/**
 * Background→foreground resume detector. While a tab is hidden, requestAnimationFrame
 * is suspended (or throttled to ~1fps) but the feed keeps polling, so only the latest
 * snapshot survives in the store — every intermediate position is dropped. The next
 * visible frame would otherwise animate minutes of dropped movement compressed into a
 * single glide window, streaking vehicles across the map. We latch the first frame
 * after the page becomes visible again so every animator built that frame snaps
 * straight to the current reported position instead of gliding from a stale spot.
 *
 * Time-clamping alone can't fix this: a glide's `from` point is the minutes-old
 * position last seen before the tab was hidden, not just a stale clock — so the
 * resume must be detected explicitly and the marker snapped.
 */

// Set by the visibilitychange listener the instant the tab returns to the foreground.
let resumePending = false;
// Snapshot of `resumePending` for the current frame, so every layer built this frame
// agrees on whether it is the resume frame (the flag is consumed once per frame, not
// once per read).
let resumedThisFrame = false;
let started = false;

function ensureListening(): void {
  if (started || typeof document === 'undefined') return;
  started = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resumePending = true;
  });
}

/** Call once at the top of each animation frame, before building any layer, so every
 *  layer built this frame sees the same resume flag. */
export function beginResumeFrame(): void {
  ensureListening();
  resumedThisFrame = resumePending;
  resumePending = false;
}

/** True only on the first frame after the tab returned to the foreground. */
export function isResumeFrame(): boolean {
  return resumedThisFrame;
}

/** Test-only: reset the latched/pending flags between cases. */
export function __resetResumeForTest(): void {
  resumePending = false;
  resumedThisFrame = false;
}
